import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';

import { COUNTRY_CODE } from '../constants';
import SEO from '../components/SEO';
import Layout from '../components/Layout';
import SlideSection from '../components/SlideSection';
import OurProductsHighlightsSection from '../components/OurProductsHighlightsSection';
import NewsSection from '../components/NewsSection';
import VideosSection from '../components/VideoSection';
import EventsSection from '../components/EventsSection';
import GeneralHighlightsSection from '../components/GeneralHighlightsSection';
import NewsletterSection from '../components/NewsletterSection';
import BlexiaTopBar, { BlexiaTopBarProps } from '../components/BlexiaTopBar';
import BlexiaBanner, { BlexiaBannerProps } from '../components/BlexiaBanner';
import { useTranslation } from 'react-i18next';

import * as styles from './index.module.scss';

interface QueryData {
  site: {
    siteMetadata: {
      hasNewsletter?: boolean;
    };
  };
  sanityHomepage?: {
    slides: Array<{
      _key: string;
    }>;
    ourProductsHighlights: Array<{
      _key: string;
    }>;
    news: Array<{
      _key: string;
    }>;
    videos: Array<{
      _key: string;
      title: string;
      url: string;
      date?: string;
    }>;
    events: Array<{
      _key: string;
    }>;
    generalHighlights: Array<{
      _key: string;
    }>;
  };
  sanityBlexia?: {
    homepageTopBar:
      | {
          enabled?: false;
        }
      | ({
          enabled: true;
        } & BlexiaTopBarProps);
    homepageBanner:
      | {
          enabled?: false;
        }
      | ({
          enabled: true;
        } & BlexiaBannerProps);
  };
}

const IndexPage = (): React.ReactElement => {
  const data: QueryData = useStaticQuery(
    graphql`
      {
        site {
          siteMetadata {
            hasNewsletter
          }
        }
        sanityHomepage {
          slides {
            _key
          }
          ourProductsHighlights {
            _key
          }
          news {
            _key
          }
          videos {
            _key
            title
            url
            date
          }
          events {
            _key
          }
          generalHighlights {
            _key
          }
        }
        sanityBlexia {
          homepageTopBar {
            enabled
            ...BlexiaTopBar
          }
          homepageBanner {
            enabled
            ...BlexiaBanner
          }
        }
      }
    `,
  );
  const { hasNewsletter } = data.site.siteMetadata;
  const homepage = data.sanityHomepage;
  const blexia = data.sanityBlexia;

  const { t } = useTranslation();

  return (
    <>
      {blexia?.homepageTopBar.enabled && <BlexiaTopBar {...blexia.homepageTopBar} />}
      <Layout className="homepage">
        <SEO />
        {COUNTRY_CODE === 'fr' && (
          <div className={styles.bannerFR}>
            <span>Site Réservé aux Professionnels de L'agriculture</span>
          </div>
        )}
        {!!homepage?.slides && homepage.slides.length > 0 && <SlideSection></SlideSection>}
        {!!homepage?.ourProductsHighlights && homepage.ourProductsHighlights.length > 0 && (
          <OurProductsHighlightsSection></OurProductsHighlightsSection>
        )}
        {blexia?.homepageBanner.enabled && <BlexiaBanner {...blexia.homepageBanner} />}
        {!!homepage?.news && homepage.news.length > 0 && <NewsSection></NewsSection>}
        {!!homepage?.videos && homepage.videos.length > 0 && (
          <VideosSection
            videos={homepage.videos}
            title={t('video_section.title', 'ASCENZA TV Channel')}
            videosPerPage={1000000}
          ></VideosSection>
        )}
        {!!homepage?.events && homepage.events.length > 0 && (
          <EventsSection greenBackground></EventsSection>
        )}
        {!!homepage?.generalHighlights && homepage.generalHighlights.length > 0 && (
          <GeneralHighlightsSection></GeneralHighlightsSection>
        )}
        {hasNewsletter && <NewsletterSection greenBackground></NewsletterSection>}
      </Layout>
    </>
  );
};

export default IndexPage;
